import { calcEmploymentStart, calcIssueDate, calcService, formatDate, formatDateOrdinal, genCertificateNo, numToWords, subtractYearsMonths, workingDayOnOrBefore } from "./helpers";

// PH holidays (MM-DD) for working day calculation
const PH_HOLIDAYS = [
  "01-01","04-09","05-01","06-12","08-21","08-26",
  "11-01","11-30","12-08","12-24","12-25","12-30","12-31"
];
const isNonWorking = (d) => {
  const day = d.getDay();
  if (day === 0 || day === 6) return true;
  const key = `${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  return PH_HOLIDAYS.includes(key);
};

// Go back N working days from a given date
function goBackWorkingDays(fromDate, days) {
  const d = new Date(fromDate);
  let count = 0;
  while (count < days) {
    d.setDate(d.getDate() - 1);
    if (!isNonWorking(d)) count++;
  }
  return d;
}

// Honorific
const honorific = (gender, civilStatus) => {
  if (gender === "Female") return civilStatus === "Married" ? "Mrs." : "Ms.";
  return "Mr.";
};

// Pronoun
const pronoun = (gender) => {
  if (gender === "Female") return { subj: "She", obj: "her", poss: "her" };
  return { subj: "He", obj: "him", poss: "his" };
};

// ===== Four job templates =====
export const TEMPLATES = {
  "Laundry/Linen": {
    company: "Pureflow Laundry Station",
    role: "Laundry Worker",
    longRole: "Laundry Worker / Laundry Machine Operator",
    duties: "washing, drying, ironing, folding, and sorting laundry items; operating laundry machines; and ensuring the cleanliness and proper handling of garments in accordance with company standards",
    coeDuties: "washing, drying, ironing, folding, and sorting laundry items; operating laundry machines; and ensuring the cleanliness and proper handling of garments in accordance with company standards",
    endorseAs: "Laundry Worker",
    industry: "laundry industry",
    expertise: "laundry operations",
    manager: "Gerald Durano",
    dtiCert: "7405689",
    issueCity: "Poblacion, Asturias, Cebu",
    employerStatus: "current" // "current" or "former"
  },
  "FoodPro/MeatPro": {
    company: "AGRIPRIME MEAT AND VEGETABLE TRADING",
    role: "Food Processing and Quality Control Worker",
    longRole: "Food Processing and Quality Control Worker",
    duties: "food preparation, meat and vegetable processing, packaging, labeling, and ensuring compliance with company quality and food safety standards",
    coeDuties: "food preparation, meat and vegetable processing, packaging, labeling, and ensuring compliance with company quality and food safety standards",
    endorseAs: "Food Processor",
    industry: "food processing industry",
    expertise: "food handling and production",
    manager: "Junnel Rallos Solon",
    dtiCert: "7405764",
    issueCity: "Looc, Buanoy, Balamban, Cebu",
    employerStatus: "former"
  },
  // Same employer/letterhead as FoodPro/MeatPro (AGRIPRIME), different position
  "Agricultural Farming": {
    company: "AGRIPRIME MEAT AND VEGETABLE TRADING",
    role: "Agricultural Farm Worker",
    longRole: "Agricultural Farm Worker",
    duties: "land preparation, planting, cultivating, and harvesting vegetables and crops; applying fertilizers and irrigation; operating basic farm tools and equipment; and sorting and packing farm produce in accordance with company quality standards",
    coeDuties: "land preparation, planting, cultivating, and harvesting vegetables and crops; applying fertilizers and irrigation; operating basic farm tools and equipment; and sorting and packing farm produce in accordance with company quality standards",
    endorseAs: "Agricultural Farm Worker",
    industry: "agriculture industry",
    expertise: "crop production and farm operations",
    manager: "Junnel Rallos Solon",
    dtiCert: "7405764",
    issueCity: "Looc, Buanoy, Balamban, Cebu",
    employerStatus: "former"
  },
  "Baking/Pastry": {
    company: "Sofbake Bread and Pastries",
    role: "Baker",
    longRole: "Baker",
    duties: "dough preparation, baking bread and pastries, operating baking equipment and ovens, measuring ingredients, ensuring product quality, and maintaining cleanliness and sanitation in accordance with company food safety and quality standards",
    coeDuties: "dough preparation, baking bread and pastries, operating baking equipment and ovens, measuring ingredients, ensuring product quality, and maintaining cleanliness and sanitation in accordance with company food safety and quality standards",
    endorseAs: "Baker",
    industry: "baking and food production industry",
    expertise: "baking and food preparation",
    manager: "DANIEL NOEL",
    dtiCert: "7405689",
    issueCity: "Poblacion, Asturias, Cebu",
    employerStatus: "current"
  }
};

// Graduations from this year onward are too recent to base work experience on,
// so the secondary (high school) graduation is used instead.
const RECENT_GRAD_YEAR = 2024;

// Graduation the employment start date is based on ({ year, month }), or null when there's no usable date
function resolveGraduation(applicant) {
  const ed = applicant.education || {};
  const lg = applicant.lastGraduation;
  // Latest graduation: the form's "last graduated" field, else the highest level that has a year
  const latestLevel = [ed.college, ed.secondary, ed.elementary].find((e) => e && e.yearGraduated);
  const latest = lg && lg.year
    ? { year: lg.year, month: lg.month }
    : latestLevel && { year: latestLevel.yearGraduated, month: latestLevel.monthGraduated };
  if (!latest) return null;
  if (Number(latest.year) < RECENT_GRAD_YEAR) return latest;
  // Graduated 2024 onwards — base on secondary graduation instead
  const sec = ed.secondary;
  if (sec && sec.yearGraduated && Number(sec.yearGraduated) < RECENT_GRAD_YEAR) {
    return { year: sec.yearGraduated, month: sec.monthGraduated };
  }
  return null;
}

// Resolve employment start date from applicant data
function resolveStartDate(applicant, asOf = new Date()) {
  const grad = resolveGraduation(applicant);
  if (grad) return calcEmploymentStart(grad.year, grad.month);
  // Default: 2 years 8 months before asOf
  return calcEmploymentStart(null, null, asOf);
}

// True when the displayed COE duration should be the randomized one (see
// buildDocumentData's adjustedDuration param): real tenure as of `asOf` is
// 3+ years, or there's no usable graduation date to base it on.
export function needsDurationAdjustment(applicant, asOf = new Date()) {
  if (!resolveGraduation(applicant)) return true;
  return calcService(resolveStartDate(applicant, asOf), asOf).years >= 3;
}

export function buildDocumentData(applicant, jobKeyOrTemplate, applicationDate = new Date(), adjustedDuration = null, certificateNo = null) {
  // Accept either a hardcoded key ("Laundry/Linen") or a template object from Firestore
  const tpl = typeof jobKeyOrTemplate === "string"
    ? TEMPLATES[jobKeyOrTemplate]
    : jobKeyOrTemplate;
  if (!tpl) return null;
  // coeDuties falls back to duties when not set separately
  if (!tpl.coeDuties) tpl.coeDuties = tpl.duties;

  const fullName = `${applicant.firstName || ""} ${applicant.middleName ? applicant.middleName.charAt(0) + ". " : ""}${applicant.lastName || ""}`.trim();
  const lastName = applicant.lastName || "";
  const titledName = `${honorific(applicant.gender, applicant.civilStatus)} ${fullName}`;
  const titledLast = `${honorific(applicant.gender, applicant.civilStatus)} ${lastName}`;
  // Endorsement Letter date = endorsement request date
  const issueDate = applicationDate instanceof Date ? applicationDate : new Date(applicationDate);
  // COE issue date = 3 weeks (15 working days) BEFORE the endorsement request date
  const coeIssueDate = goBackWorkingDays(issueDate, 15);
  // Service is counted up to the COE issue date ("As of this date..."), not today,
  // so regenerating the documents later gives the same start date and duration.
  const realStartDate = resolveStartDate(applicant, coeIssueDate);
  const realService = calcService(realStartDate, coeIssueDate);
  // Applicants who graduated long ago (3+ real years) or have no usable graduation
  // date get a randomized 2y2m-2y8m duration instead — the synthetic start date
  // below (moved back to a working day) keeps the two consistent so the document
  // never shows a start date that contradicts the years shown.
  const useAdjusted = !!adjustedDuration && needsDurationAdjustment(applicant, coeIssueDate);
  const years = useAdjusted ? adjustedDuration.years : realService.years;
  const months = useAdjusted ? adjustedDuration.months : realService.months;
  const startDate = useAdjusted ? workingDayOnOrBefore(subtractYearsMonths(coeIssueDate, years, months)) : realStartDate;
  const todayDate = new Date();
  const jobKey = typeof jobKeyOrTemplate === "string" ? jobKeyOrTemplate : (tpl.role || "");
  // Persisted per job by the admin page; random fallback only until it's saved
  const certNo = certificateNo || applicant.certificates?.[jobKey] || genCertificateNo(todayDate.getFullYear());

  return {
    job: jobKey,
    template: tpl,
    fullName, lastName, titledName, titledLast,
    pronoun: pronoun(applicant.gender),
    startDateFormatted: formatDate(startDate),
    issueDate, issueDateFormatted: formatDate(issueDate), issueDateOrdinal: formatDateOrdinal(issueDate),
    coeIssueDate, coeIssueDateFormatted: formatDate(coeIssueDate), coeIssueDateOrdinal: formatDateOrdinal(coeIssueDate),
    yearsService: years, monthsService: months,
    yearsWord: numToWords(years), monthsWord: numToWords(months),
    certificateNo: certNo
  };
}
